import { useCallback, useEffect, useRef } from "react";

const learningTtsHttpUrl = (): string =>
  typeof window === "undefined" ? "/api/audio/learning-tts" : `${window.location.origin}/api/audio/learning-tts`;

const learningTtsStreamHttpUrl = (): string =>
  typeof window === "undefined"
    ? "/api/audio/learning-tts/stream"
    : `${window.location.origin}/api/audio/learning-tts/stream`;

const MPEG_MSE_TYPE = 'audio/mpeg; codecs="mp3"';

type HtmlAudioWithLegacyPitch = HTMLAudioElement & {
  mozPreservesPitch?: boolean;
  webkitPreservesPitch?: boolean;
};

/** Prefer speed change without shifting pitch (browser-dependent; no-op where unsupported). */
const applyPlaybackRatePreservingPitch = (audio: HTMLAudioElement, rate: number) => {
  const el = audio as HtmlAudioWithLegacyPitch;

  el.preservesPitch = true;

  if ("mozPreservesPitch" in el) {
    el.mozPreservesPitch = true;
  }
  if ("webkitPreservesPitch" in el) {
    el.webkitPreservesPitch = true;
  }
  el.playbackRate = rate;
};

export function useLearningMessageTts({
  getToken,
  playbackRate = 1,
}: {
  getToken: () => Promise<string | null>;
  playbackRate?: number;
}) {
  const requestAbortRef = useRef<AbortController | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const streamObjectUrlRef = useRef<string | null>(null);
  const handlersRef = useRef<{
    onPlaybackEnd?: () => void;
    onError?: () => void;
    onPlaybackStart?: () => void;
    onMetadata?: (durationSeconds: number) => void;
    onTimeUpdate?: (currentTimeSeconds: number, durationSeconds: number) => void;
  }>({});
  const suppressNextAudioErrorRef = useRef(false);
  const playbackRateRef = useRef(playbackRate);
  playbackRateRef.current = playbackRate;

  const revokeStreamObjectUrl = useCallback(() => {
    if (streamObjectUrlRef.current) {
      URL.revokeObjectURL(streamObjectUrlRef.current);
      streamObjectUrlRef.current = null;
    }
  }, []);

  const teardownAudioElement = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    suppressNextAudioErrorRef.current = true;
    audio.onended = null;
    audio.onerror = null;
    audio.onloadedmetadata = null;
    audio.onplaying = null;
    audio.ontimeupdate = null;
    audio.pause();
    audio.src = "";
    audioRef.current = null;
    revokeStreamObjectUrl();
  }, [revokeStreamObjectUrl]);

  const stopSession = useCallback(() => {
    if (requestAbortRef.current) {
      requestAbortRef.current.abort();
      requestAbortRef.current = null;
    }
    teardownAudioElement();
  }, [teardownAudioElement]);

  const pauseSession = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const resumeSession = useCallback(() => {
    if (!audioRef.current) {
      return;
    }
    void audioRef.current.play().catch(() => {
      handlersRef.current.onError?.();
    });
  }, []);

  const setHandlers = useCallback(
    (next: {
      onPlaybackEnd?: () => void;
      onError?: () => void;
      onPlaybackStart?: () => void;
      onMetadata?: (durationSeconds: number) => void;
      onTimeUpdate?: (currentTimeSeconds: number, durationSeconds: number) => void;
    }) => {
      handlersRef.current = next;
    },
    []
  );

  const wireAudioElement = useCallback((audio: HTMLAudioElement) => {
    suppressNextAudioErrorRef.current = false;
    applyPlaybackRatePreservingPitch(audio, playbackRateRef.current);
    audioRef.current = audio;
    audio.onended = () => {
      handlersRef.current.onPlaybackEnd?.();
    };
    audio.onerror = () => {
      if (suppressNextAudioErrorRef.current) {
        suppressNextAudioErrorRef.current = false;
        return;
      }
      handlersRef.current.onError?.();
    };
    audio.onloadedmetadata = () => {
      if (Number.isFinite(audio.duration)) {
        handlersRef.current.onMetadata?.(audio.duration);
      }
      if (Number.isFinite(audio.currentTime) && Number.isFinite(audio.duration)) {
        handlersRef.current.onTimeUpdate?.(audio.currentTime, audio.duration);
      }
    };
    audio.onplaying = () => {
      handlersRef.current.onPlaybackStart?.();
    };
    audio.ontimeupdate = () => {
      if (!Number.isFinite(audio.currentTime)) {
        return;
      }
      const durationSeconds = Number.isFinite(audio.duration) ? audio.duration : 0;
      handlersRef.current.onTimeUpdate?.(audio.currentTime, durationSeconds);
    };

    // If metadata is already available when wiring (e.g. if `src` was swapped), emit it once.
    if (audio.readyState >= 1) {
      if (Number.isFinite(audio.duration)) {
        handlersRef.current.onMetadata?.(audio.duration);
      }
      if (Number.isFinite(audio.currentTime)) {
        const durationSeconds = Number.isFinite(audio.duration) ? audio.duration : 0;
        handlersRef.current.onTimeUpdate?.(audio.currentTime, durationSeconds);
      }
    }
  }, []);

  const startSession = useCallback(
    async (plainText: string, messageId?: string) => {
      stopSession();
      const trimmed = plainText.trim();
      if (!trimmed) {
        return;
      }

      const token = await getToken();
      if (!token) {
        handlersRef.current.onError?.();
        return;
      }
      const abortController = new AbortController();
      requestAbortRef.current = abortController;
      const url = learningTtsHttpUrl();
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ text: trimmed, token, messageId }),
          signal: abortController.signal,
          redirect: "error",
        });

        if (!response.ok) {
          handlersRef.current.onError?.();
          return;
        }

        const data = (await response.json()) as { audioUrl?: string };
        if (!data.audioUrl || abortController.signal.aborted || requestAbortRef.current !== abortController) {
          handlersRef.current.onError?.();
          return;
        }

        if (typeof window === "undefined") {
          handlersRef.current.onError?.();
          return;
        }

        const audio = new Audio(
          data.audioUrl.startsWith("http") ? data.audioUrl : `${window.location.origin}${data.audioUrl}`
        );
        wireAudioElement(audio);

        await audio.play();
      } catch (err) {
        if (!abortController.signal.aborted) {
          handlersRef.current.onError?.();
        }
      } finally {
        if (requestAbortRef.current === abortController) {
          requestAbortRef.current = null;
        }
      }
    },
    [getToken, stopSession, wireAudioElement]
  );

  const playMp3WithMse = useCallback(
    async (reader: ReadableStreamDefaultReader<Uint8Array>, audio: HTMLAudioElement) => {
      const mediaSource = new MediaSource();
      const objectUrl = URL.createObjectURL(mediaSource);
      streamObjectUrlRef.current = objectUrl;
      audio.src = objectUrl;

      await new Promise<void>((resolve, reject) => {
        mediaSource.addEventListener("sourceopen", () => resolve(), { once: true });
        mediaSource.addEventListener("error", () => reject(new Error("MediaSource error")), { once: true });
      });

      const mime = MediaSource.isTypeSupported(MPEG_MSE_TYPE) ? MPEG_MSE_TYPE : "audio/mpeg";
      const sourceBuffer = mediaSource.addSourceBuffer(mime);
      let appended = false;

      try {
        let readResult = await reader.read();
        while (!readResult.done) {
          const { value } = readResult;
          appended = true;
          await new Promise<void>((resolve, reject) => {
            const onUpdateEnd = () => {
              sourceBuffer.removeEventListener("updateend", onUpdateEnd);
              sourceBuffer.removeEventListener("error", onSbErr);
              resolve();
            };
            const onSbErr = () => {
              sourceBuffer.removeEventListener("updateend", onUpdateEnd);
              reject(new Error("SourceBuffer error"));
            };
            sourceBuffer.addEventListener("updateend", onUpdateEnd, { once: true });
            sourceBuffer.addEventListener("error", onSbErr, { once: true });
            try {
              sourceBuffer.appendBuffer(value);
            } catch (appendErr) {
              sourceBuffer.removeEventListener("updateend", onUpdateEnd);
              sourceBuffer.removeEventListener("error", onSbErr);
              reject(appendErr);
            }
          });
          readResult = await reader.read();
        }
        if (!appended) {
          throw new Error("Empty audio stream");
        }
        if (mediaSource.readyState === "open") {
          mediaSource.endOfStream();
        }
      } catch {
        throw new Error("MSE append failed");
      }
    },
    []
  );

  const playMp3FromFullBlob = useCallback(
    async (reader: ReadableStreamDefaultReader<Uint8Array>, audio: HTMLAudioElement) => {
      const parts: BlobPart[] = [];
      let readResult = await reader.read();
      while (!readResult.done) {
        parts.push(readResult.value);
        readResult = await reader.read();
      }
      const blob = new Blob(parts, { type: "audio/mpeg" });
      const objectUrl = URL.createObjectURL(blob);
      streamObjectUrlRef.current = objectUrl;
      audio.src = objectUrl;
    },
    []
  );

  const startStreamSession = useCallback(
    async (plainText: string, messageId?: string) => {
      stopSession();
      const trimmed = plainText.trim();
      if (!trimmed) {
        return;
      }

      const token = await getToken();
      if (!token) {
        handlersRef.current.onError?.();
        return;
      }

      if (typeof window === "undefined") {
        handlersRef.current.onError?.();
        return;
      }

      const abortController = new AbortController();
      requestAbortRef.current = abortController;

      try {
        const response = await fetch(learningTtsStreamHttpUrl(), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token, messageId, text: trimmed }),
          signal: abortController.signal,
          redirect: "error",
        });

        if (!response.ok || abortController.signal.aborted || requestAbortRef.current !== abortController) {
          handlersRef.current.onError?.();
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) {
          handlersRef.current.onError?.();
          return;
        }

        const audio = new Audio();
        wireAudioElement(audio);

        const canMse =
          typeof MediaSource !== "undefined" &&
          (MediaSource.isTypeSupported(MPEG_MSE_TYPE) || MediaSource.isTypeSupported("audio/mpeg"));

        try {
          if (canMse) {
            await playMp3WithMse(reader, audio);
          } else {
            await playMp3FromFullBlob(reader, audio);
          }
        } catch {
          handlersRef.current.onError?.();
          return;
        }

        if (abortController.signal.aborted) {
          return;
        }

        // Setting `src` can reset rate / pitch flags on some browsers; re-apply after source is ready.
        applyPlaybackRatePreservingPitch(audio, playbackRateRef.current);
        await audio.play();
      } catch (err) {
        if (!abortController.signal.aborted) {
          handlersRef.current.onError?.();
        }
      } finally {
        if (requestAbortRef.current === abortController) {
          requestAbortRef.current = null;
        }
      }
    },
    [getToken, playMp3FromFullBlob, playMp3WithMse, stopSession, wireAudioElement]
  );

  useEffect(() => {
    if (audioRef.current) {
      applyPlaybackRatePreservingPitch(audioRef.current, playbackRate);
    }
  }, [playbackRate]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }
    const handlePageHidden = () => {
      stopSession();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopSession();
      }
    };
    window.addEventListener("pagehide", handlePageHidden);
    window.addEventListener("beforeunload", handlePageHidden);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", handlePageHidden);
      window.removeEventListener("beforeunload", handlePageHidden);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [stopSession]);

  return { startSession, startStreamSession, stopSession, pauseSession, resumeSession, setHandlers };
}
